import { idDeLaReferencia, type Tarjeta } from '@athena/shared';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';

/**
 * Las dos caras de una comparación, con sus cuatro campos de transparencia.
 *
 * Los cuatro campos **los rellena el motor desde la ficha de la fuente**, nunca el modelo, y lo que
 * la ficha no trae sale como "No lo dice". Es lo que hace que dos cifras puestas una al lado de
 * otra enseñen por qué se pueden comparar o por qué no, en vez de pedirle al lector que se fíe.
 *
 * En móvil se apilan (`plan/F4-web.md`): dos columnas de 180 píxeles no se leen, y lo que se pierde
 * al apilar es la comparación de un vistazo, que es justo lo que este bloque existe para dar.
 */
export function TarjetasLadoALado({
  tarjetas,
  respuesta,
  idioma,
}: {
  readonly tarjetas: readonly Tarjeta[];
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  const t = diccionarioDe(idioma).consulta;

  return (
    <div className='mb-[18px] grid grid-cols-1 gap-4 min-[861px]:grid-cols-2'>
      {tarjetas.map(tarjeta => {
        const fuente = respuesta.fuentes.find(f => f.hechoId === idDeLaReferencia(tarjeta.afirmacion));
        return (
          <article key={tarjeta.afirmacion} className='rounded-lg border border-(--line-strong) p-[18px]'>
            <h3 className='m-0 mb-[6px] font-(family-name:--text) text-[13px] font-semibold tracking-[0.04em] text-(--ink-faint) uppercase'>
              {tarjeta.etiqueta}
            </h3>
            {fuente?.cifra === null || fuente === undefined ? null : (
              <p className='m-0 mb-2 font-(family-name:--serif) text-[32px] leading-none font-bold text-(--teal-deep)'>{fuente.cifra}</p>
            )}
            <p className='m-0 mb-[14px] font-(family-name:--text) text-[15px] leading-[1.55] text-(--ink)'>{tarjeta.frase}</p>
            <dl className='m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-[6px] font-(family-name:--text) text-[12px]'>
              <Campo etiqueta={t.transparencia.quien} valor={fuente?.editor ?? null} noLoDice={t.transparencia.noLoDice} />
              <Campo etiqueta={t.transparencia.aQuien} valor={fuente?.poblacion ?? null} noLoDice={t.transparencia.noLoDice} />
              <Campo
                etiqueta={t.transparencia.cuando}
                valor={fuente?.anio === null || fuente === undefined ? null : String(fuente.anio)}
                noLoDice={t.transparencia.noLoDice}
              />
              {/*
                Estaba cableado a `null`, así que decía «no lo dice» aunque la ficha lo dijera. No
                faltaba el dato: no se pedía. Lo vio una revisión adversarial el 2026-09-22.
              */}
              <Campo etiqueta={t.transparencia.quienPaga} valor={fuente?.financiador ?? null} noLoDice={t.transparencia.noLoDice} />
            </dl>
          </article>
        );
      })}
    </div>
  );
}

function Campo({ etiqueta, valor, noLoDice }: { readonly etiqueta: string; readonly valor: string | null; readonly noLoDice: string }) {
  return (
    <>
      <dt className='text-(--ink-faint)'>{etiqueta}</dt>
      <dd className={`m-0 ${valor === null ? 'text-(--ink-faint) italic' : 'text-(--ink-soft)'}`}>{valor ?? noLoDice}</dd>
    </>
  );
}
